import type { JourneyFlow } from '../state/journeyFlow';
import type { ReadingPreferences } from '../state/readingPreferences';

/**
 * Every localStorage key the original runtime owns (app/app.js). Extracted
 * React features share this storage with the not-yet-extracted legacy code,
 * so the key names and value shapes below are a compatibility contract —
 * storageContract.test.ts pins them against the legacy source.
 */
export const STORAGE_KEYS = {
  /** Partial ReadingPreferences merged over defaults at load. */
  readingPreferences: 'qc2_ui',
  /** Verse keys ("surah:ayah") confirmed read, persisted as an array. */
  confirmedVerseKeys: 'qc2_conf',
  /** Completed page numbers, persisted as an array. */
  completedPages: 'qc2_pages',
  /** The page currently being read; null means "no page assigned yet". */
  currentPage: 'qc2_page',
  /** Pages read since the thank-you screen last showed, persisted as an array. */
  streakPages: 'qc2_streak',
  /** Reflection-video ids already watched on this device, persisted as an array. */
  watchedVideoIds: 'qc2_watched',
  /** Monotonic per-video progress records, keyed by video id. */
  videoProgress: 'qc2_video_progress',
  /** Selected reciter id (quran.com recitation id); 0 means "not chosen yet". */
  reciterId: 'qc2_reciter',
  /** 1 once the reading-preferences onboarding has been completed. */
  setupDone: 'qc2_setup',
  /** Journey flow: intro/account/intention plus donation bookkeeping. */
  journeyFlow: 'qc2_flow',
  /** Supabase session: {a: accessToken, r: refreshToken|null, e: email|null} or null. */
  supabaseSession: 'qc2_sb',
  /** Reading context stashed across an auth redirect, or null. */
  pendingAuth: 'qc2_pending_auth',
  /** Millisecond timestamp of the last local preferences change (account sync). */
  preferencesUpdatedAt: 'qc2_ui_ts',
  /** true once the PWA install chip has been dismissed. */
  installDismissed: 'qc2_install_dismissed',
  /** ISO dates ("YYYY-MM-DD") with at least one finished page — drives the streak. */
  readingDays: 'qc2_days',
} as const;

/**
 * The keys the "Reset demo" chip clears (app/app.js resetChip handler).
 * Deliberately NOT all keys: session, pending auth, watch history, video
 * progress, sync timestamp and install dismissal survive a demo reset.
 */
export const DEMO_RESET_KEYS: readonly string[] = [
  STORAGE_KEYS.readingPreferences,
  STORAGE_KEYS.confirmedVerseKeys,
  STORAGE_KEYS.completedPages,
  STORAGE_KEYS.currentPage,
  STORAGE_KEYS.streakPages,
  STORAGE_KEYS.journeyFlow,
  STORAGE_KEYS.setupDone,
  STORAGE_KEYS.reciterId,
  STORAGE_KEYS.readingDays,
];

/** Stored shape of one reflection video's progress (qc2_video_progress values). */
export interface StoredVideoProgress {
  watchedSeconds: number;
  durationSeconds: number;
  progressPercent: number;
  completedAt: string | null;
  lastWatchedAt?: string;
}

/** Stored shape of the Supabase session (qc2_sb). */
export interface StoredSupabaseSession {
  a: string;
  r: string | null;
  e: string | null;
}

/** Reading context carried across a sign-in redirect (qc2_pending_auth). */
export interface StoredPendingAuth {
  from: string;
  page?: number;
  gave?: boolean;
  mailingList?: boolean;
}

/** The value types behind each storage key, for typed read/write helpers. */
export interface StorageSchema {
  qc2_ui: Partial<ReadingPreferences>;
  qc2_conf: string[];
  qc2_pages: number[];
  qc2_page: number | null;
  qc2_streak: number[];
  qc2_watched: string[];
  qc2_video_progress: Record<string, StoredVideoProgress>;
  qc2_reciter: number;
  qc2_setup: 0 | 1;
  qc2_flow: Partial<JourneyFlow>;
  qc2_sb: StoredSupabaseSession | null;
  qc2_pending_auth: StoredPendingAuth | null;
  qc2_ui_ts: number;
  qc2_install_dismissed: boolean;
  qc2_days: string[];
}
